import * as fs from 'fs';
import * as path from 'path';
import * as utils from '../utils';
import { promisify } from 'util';
import { spawn, exec as _exec, SpawnOptions } from 'child_process';
import { getLogger } from './logger';
import { additionalLogDetails } from './logUtils';
import { requireWithFallback } from './requireWithFallback';
import { IncompatibleNpmPackageError, NpmPackageError, NpmPermissionsError, TimeoutError } from '../errors';

const exec = promisify(_exec);
const logger = getLogger('cli-service');

function getPathWithMissingPermissions(error: any) {
    const pathRegex = /EACCES[^']+'(.+)'/;
    const regexResult = pathRegex.exec(error);
    if (regexResult === null) {
        return false;
    }
    return regexResult[1];
}

// https://github.com/npm/arborist/pull/362
function npmSevenAndEightMissingPermissions(error: any) {
    return /The "to" argument must be of type string./.exec(error);
}

function checkForInstallationAccessError(error: any, defaultRoot: string, packageName?: string) {
    const pathWithMissingPermissions = getPathWithMissingPermissions(error);
    const npmEightMissingPermissions = npmSevenAndEightMissingPermissions(error);
    if (pathWithMissingPermissions || npmEightMissingPermissions) {
        logger.info('Failed to install package due to insufficient write access', {
            ...additionalLogDetails(),
            package: packageName,
            path: pathWithMissingPermissions,
        });
        // eslint-disable-next-line no-console
        console.error(`

Testim failed installing the package ${packageName || ''} due to insufficient permissions.
This is probably due to an installation of @testim/testim-cli with sudo, and running it without sudo.
Testim had missing write access to ${pathWithMissingPermissions || defaultRoot}

`);
        return true;
    }
    return false;
}

export async function getLatestPackageVersion(packageName: string) {
    const result = await exec(`npm view ${packageName} version`);
    return result.stdout.trim();
}

export function getPackageIfInstalledLocally(packageName: string) {
    try {
        // note: this code fails if require itself throws here
        // rather than an ENOENT - but since lazy dependencies are all controlled
        // by us that's probably fine.
        return requireWithFallback(packageName);
    } catch (err) {
        return false;
    }
}

export async function isPackageInstalledInPath(rootPath: string, packageName: string) {
    try {
        return await utils.doesPathExist(path.join(rootPath, `./node_modules/${packageName}/package.json`));
    } catch (error) {
        return false;
    }
}

export function getLocallyInstalledPackageVersion(rootPath: string, packageName: string): string {
    const packageJsonPath = path.join(rootPath, `./node_modules/${packageName}/package.json`);
    const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
    return packageJson.version;
}

export function extractCommonJsPathFromPackageJson(packageManifest: Record<string, unknown> | string): string | undefined {
    if (typeof packageManifest === 'string' && packageManifest.endsWith('.cjs')) {
        return packageManifest;
    }

    if (typeof packageManifest === 'object' && packageManifest !== null) {
        for (const key of Object.keys(packageManifest).filter(key => key !== 'browser')) {
            const pathFromManifest = extractCommonJsPathFromPackageJson(packageManifest[key] as Record<string, unknown> | string);

            if (pathFromManifest) {
                return pathFromManifest;
            }
        }
    }

    return undefined;
}

export function getLocallyInstalledCommonJsPath(packagePath: string): string {
    const packageJsonPath = path.join(packagePath, 'package.json');
    const packageManifest = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));

    if (!('exports' in packageManifest)) {
        return '';
    }

    // The package has an "exports" field, which mean it support multiple exports types. We need to find the commonjs export path and use that one as the package path as cli action support packages
    // with common js export.
    const commonJSExportPath = extractCommonJsPathFromPackageJson(packageManifest);

    if (!commonJSExportPath) {
        throw new IncompatibleNpmPackageError(packageManifest.name);
    }

    return commonJSExportPath;
}

export async function installPackageLocally(rootPath: string, packageName: string, execOptions?: Parameters<typeof exec>[1]) {
    // this is here because our shrinkwrap blocks our lazy deps for some reason
    const oldShrinkwrap = path.join(rootPath, 'npm-shrinkwrap.json');
    const newShrinkwrap = path.join(rootPath, 'npm-shrinkwrap-dummy.json');
    let renamed = false;
    try {
        try {
            if (await utils.doesPathExist(oldShrinkwrap)) {
                await fs.promises.rename(oldShrinkwrap, newShrinkwrap);
                renamed = true;
            }
        } catch (err) {
            // ignore error
        }
        return await exec(`npm i ${packageName} --no-save --no-prune --prefer-offline --no-audit --progress=false`, { ...execOptions, cwd: rootPath }).catch(err => {
            const accessError = checkForInstallationAccessError(err.stderr, rootPath, packageName);
            if (accessError) {
                throw new NpmPermissionsError(`${rootPath} Failed to install package: ${packageName} due to insufficient write access in directory`);
            }
            throw err;
        });
    } finally {
        if (renamed) {
            try {
                await fs.promises.rename(newShrinkwrap, oldShrinkwrap);
            } catch (err) {
                // ignore error
            }
        }
    }
}

export function installPackages(prefix: string, packageNames: string[], proxyUri: string | undefined, timeoutMs: number, useCache = true) {
    return new Promise<string>((resolve, reject) => {
        const proxyFlag = proxyUri ? ['--proxy', proxyUri] : [];

        const spawnOptions: SpawnOptions = proxyUri ? { env: Object.assign(process.env, { HTTP_PROXY: proxyUri, HTTPS_PROXY: proxyUri }) } : {};

        let stdout = '';
        let stderr = '';

        const options = [
            '--no-save',
            '--no-package-lock',
            '--no-prune',
            '--no-audit',
            '--progress=false',
        ];
        if (useCache) {
            options.push( '--prefer-offline');
        }

        let npmCmd = 'npm';
        if (process.platform === 'win32') {
            // Check if we run on win32 https://nodejs.org/api/process.html#processplatform
            // And run npm.cmd in shell: true. See here why: https://nodejs.org/api/child_process.html#child_process_spawning_bat_and_cmd_files_on_windows
            npmCmd = 'npm.cmd';
            spawnOptions.shell = true;
        }
        const npmInstall = spawn(npmCmd, ['i', '--prefix', prefix, ...options, ...packageNames, ...proxyFlag], spawnOptions);
        npmInstall.stderr?.pipe(process.stderr);
        npmInstall.stdout?.pipe(process.stdout);

        npmInstall.stdout?.on('data', (data) => {
            stdout += data;
        });

        npmInstall.stderr?.on('data', (data) => {
            stderr += data;
        });

        npmInstall.on('close', (code) => {
            if (code) {
                // This is a retry tor the installation because in some cases when using --prefer-offline flag it only checks the cache and not the registry
                // so we need to retry the installation without the --prefer-offline flag to check in the registry before failing.
                // If the retry succeeds we resolve the promise with the stdout or else we reject with the error
                if (useCache) installPackages(prefix, packageNames, proxyUri, timeoutMs, false).then(resolve).catch(reject);
                let errorString;
                try {
                    const packageNotInRegistryError = /npm ERR! 404 {2}'(.+)' is not in the npm registry/gm.exec(stderr)?.[1];
                    const packageVersionNotFoundError = /No matching version found for (\S+)@/gm.exec(stderr)?.[1];
                    if (packageNotInRegistryError || packageVersionNotFoundError) {
                        const packageName = packageVersionNotFoundError ?? packageNotInRegistryError;
                        errorString = `404 Not Found - GET https://registry.npmjs.org/${packageName} - Not found`;
                    } else {
                        const accessError = checkForInstallationAccessError(stderr, prefix);
                        if (accessError) {
                            errorString = `insufficient write access in location ${prefix}`;
                        } else {
                            errorString = `Npm Installation closed with exit code ${code} with error: ${stderr}`;
                        }
                    }
                } catch (err) {
                    errorString = `Npm Install closed with exit code ${code}\n err: ${err}\n stdout: ${stdout} stderr: ${stderr}`;
                }

                logger.info(`Npm Install closed with exit code ${code}`, { message: errorString, stderr });

                reject(new NpmPackageError(errorString));
            } else {
                resolve(stdout);
            }
        });

        setTimeout(() => {
            try {
                npmInstall.kill();
            } finally {
                reject(new TimeoutError());
            }
        }, timeoutMs);
    });
}

export async function getNpmVersion() {
    try {
        const result = await exec('npm -v');
        return result.stdout.trim();
    } catch {
        return '';
    }
}
